import {useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {Accordion, Breadcrumb, Button, Card, Col, Form, InputGroup, ListGroup, Row} from 'react-bootstrap'
import {GET_ALL_GROUPS} from '../../gql/queries/assignmentGroupQuery'
import {SYNC_GROUP_TELEPHONY} from '../../../modules/assignmentGroupMutation'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'
import {IMembersNode, IResGroups} from '../../types/assignmentGroups'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalAddAssignmentGroup} from './components/ModalAddAssignmentGroup'
import clsx from 'clsx'
import {getColorMember, parseId} from '../../helpers'
import moment from 'moment'
import {cleanVariables, PaginationUtils} from '../shared/PaginationUtils'
import {SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationSection} from '../shared/PaginationSection'
import {useGetUser} from '../../store/userStore'
import {GET_ALL_ORGANIZATIONS} from '../../gql/queries/organizationsQuery'
import {
  GROUP_MESSAGES as M,
  EM_DASH,
  RING_STRATEGY_VALUES,
  ringStrategyLabel,
} from './assignmentGroupMessages'

// Nombre y correo son datos, no copy: se unen aquí para que el separador no
// quede como texto suelto dentro del JSX.
const fullName = (first?: string, last?: string) => `${first ?? ''} ${last ?? ''}`.trim()
const memberLine = (first?: string, last?: string, email?: string) =>
  `${fullName(first, last)} - ${email ?? ''}`

export const AssignmentGroupsPage = () => {
  const intl = useIntl()
  const [gqlGetGroups, {data, loading, error, refetch}] = useLazyQuery<IResGroups>(
    GET_ALL_GROUPS,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [dataArr, setDataArr] = useState<IResGroups['allGroupAssignments']['edges']>([])
  const {isOpen, onClose, onOpen} = useDisclourse()
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')

  const [syncTelephony] = useMutation(SYNC_GROUP_TELEPHONY)
  const [syncingId, setSyncingId] = useState<string | null>(null)

  const handleSyncTelephony = async (groupId: string, ringStrategy?: string) => {
    setSyncingId(groupId)
    try {
      const {data: res} = await syncTelephony({
        variables: {groupAssignmentId: parseId(groupId), ringStrategy: ringStrategy || undefined},
      })
      const out = res?.syncGroupTelephonyMutation
      if (out?.success) {
        toast.success(
          intl.formatMessage(M.toastSynced, {
            id: out.vicidialIngroupId || '',
            agents: out.agentsSynced ?? 0,
          })
        )
        if (out.skipped?.length) {
          toast.warning(
            intl.formatMessage(M.toastSkipped, {
              count: out.skipped.length,
              names: out.skipped.join(', '),
            })
          )
        }
        refetch?.()
      } else {
        toast.error(out?.errors?.[0]?.message || intl.formatMessage(M.toastSyncFailed))
      }
    } catch (e: any) {
      toast.error(e?.message || intl.formatMessage(M.toastSyncError))
    } finally {
      setSyncingId(null)
    }
  }

  const [infoGruop, setInfoGruop] = useState<null | {
    id: string
    name: string
    members: IMembersNode[]
    supervisorIds?: (string | number)[]
    organizationId?: string | number
    visibleForTickets?: boolean
  }>(null)

  const [formData, setFormData] = useState({
    text: '',
    organizationId: '',
  })

  let totalElements = data?.allGroupAssignments?.totalCount ?? 0
  const totalPages = Math.ceil(totalElements / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalElements)

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleFilters = () => {
    const variables = cleanVariables({
      first: first,
      offset: (currentPage - 1) * first,
      nameIcontains: formData.text,
      userName: formData.text, // También buscar por nombre de usuario
      organization: isSuperAdmin && formData.organizationId ? parseId(formData.organizationId) : null,
    })

    gqlGetGroups({variables})
  }

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  useEffect(() => {
    handleFilters()
  }, [gqlGetGroups, currentPage, first])

  useEffect(() => {
    if (!data) return
    const {edges} = data.allGroupAssignments
    setDataArr(edges)
  }, [data])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(M.errorWithMessage, {message: error.message}))
  }, [error])

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  useEffect(() => {
    if (errorOrganizations)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorOrganizations.message}))
  }, [errorOrganizations])

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none'>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{intl.formatMessage(M.title)}</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          <Button
            size='sm'
            onClick={() => {
              setInfoGruop(null)
              onOpen()
            }}
          >
            <i className='bi bi-plus fs-3' /> {intl.formatMessage(M.newGroup)}
          </Button>
        </div>
      </header>
      <Row className='policies'>
        <Col md={12} lg={12} sm={12} xl={12} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>{intl.formatMessage(M.allGroups)}</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder={intl.formatMessage(M.searchPlaceholder)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      text: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleFilters()
                    }
                  }}
                  value={formData.text}
                />
                {isSuperAdmin && (
                  <Form.Select
                    value={formData.organizationId}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        organizationId: e.target.value,
                      }))
                      setCurrentPage(1)
                    }}
                    disabled={loadingOrganizations}
                  >
                    {loadingOrganizations ? (
                      <option value=''>{intl.formatMessage(M.loading)}</option>
                    ) : (
                      <option value=''>{intl.formatMessage(M.allOrganizations)}</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  {intl.formatMessage(M.search)}
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setCurrentPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalElements}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>
            <div className='list-carrier table-scrollable table-container'>
              <table className='table-scrollable table-scroll table table-striped table-hover'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold' style={{width: '40px'}}></th>
                    <th className='fw-bold'>{intl.formatMessage(M.colName)}</th>
                    {isSuperAdmin && (
                      <th className='fw-bold d-none d-lg-table-cell'>
                        {intl.formatMessage(M.colOrganization)}
                      </th>
                    )}
                    <th className='fw-bold d-none d-lg-table-cell'>
                      {intl.formatMessage(M.colSupervisors)}
                    </th>
                    <th className='fw-bold d-none d-lg-table-cell text-center'>
                      {intl.formatMessage(M.colMembers)}
                    </th>
                    <th className='fw-bold d-none d-lg-table-cell text-center'>
                      {intl.formatMessage(M.colTelephony)}
                    </th>
                    <th className='fw-bold d-none d-lg-table-cell text-center'>
                      {intl.formatMessage(M.visibleForTickets)}
                    </th>
                    <th className='fw-bold d-none d-lg-table-cell'>
                      {intl.formatMessage(M.colCreated)}
                    </th>
                    <th className='fw-bold d-none d-lg-table-cell text-center'>
                      {intl.formatMessage(M.colActions)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dataArr.map(({node}) => (
                    <>
                      <tr key={node.id}>
                        <td className='px-2 text-center'>
                          <Button
                            variant='link'
                            size='sm'
                            onClick={() => toggleRow(node.id)}
                            className='p-0'
                          >
                            <i
                              className={`bi bi-chevron-${
                                expandedRows.has(node.id) ? 'down' : 'right'
                              } fs-5`}
                            />
                          </Button>
                        </td>
                        <td className='px-4'>{node.name}</td>
                        {isSuperAdmin && (
                          <td className='px-2'>
                            {node.organization ? (
                              <span>{node.organization.name}</span>
                            ) : (
                              <span className='text-muted'>{intl.formatMessage(M.noOrganization)}</span>
                            )}
                          </td>
                        )}
                        <td className='px-2'>
                          {node.supervisors && node.supervisors.edges.length > 0 ? (
                            <div>
                              {node.supervisors.edges.map(({node: supervisor}, idx) => (
                                <div key={supervisor.id}>
                                  <div className='fw-semibold'>
                                    {fullName(supervisor.firstName, supervisor.lastName)}
                                  </div>
                                  <small className='text-muted'>{supervisor.email}</small>
                                  {idx < node.supervisors.edges.length - 1 && <hr className='my-1' />}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className='text-muted'>{intl.formatMessage(M.noSupervisors)}</span>
                          )}
                        </td>
                        <td className='px-2 text-center'>{node.user.edges.length}</td>
                        <td className='px-2 text-center'>
                          {node.telephonyActive ? (
                            <div className='d-flex flex-column align-items-center'>
                              <span
                                className='badge badge-light-success mb-1'
                                title={intl.formatMessage(M.vicidialGroupTitle, {
                                  id: node.vicidialIngroupId,
                                })}
                              >
                                <i className='bi bi-telephone-inbound me-1' />
                                {intl.formatMessage(M.telephonySummary, {
                                  numbers: intl.formatMessage(M.telephonyNumbers, {
                                    count: node.assignedNumbersCount,
                                  }),
                                  agents: intl.formatMessage(M.telephonyAgents, {
                                    count: node.telephonyMembersCount,
                                  }),
                                })}
                              </span>
                              <Button
                                size='sm'
                                variant='light'
                                className='py-0 px-2'
                                disabled={syncingId === node.id}
                                onClick={() => handleSyncTelephony(node.id)}
                                title={
                                  node.vicidialSyncedAt
                                    ? intl.formatMessage(M.lastSyncTitle, {
                                        date: moment(node.vicidialSyncedAt).format('DD MMM HH:mm'),
                                      })
                                    : intl.formatMessage(M.syncTitle)
                                }
                              >
                                {syncingId === node.id ? (
                                  <span className='spinner-border spinner-border-sm' />
                                ) : (
                                  <>
                                    <i className='bi bi-arrow-repeat me-1' />
                                    {intl.formatMessage(M.sync)}
                                  </>
                                )}
                              </Button>
                              <Form.Select
                                size='sm'
                                className='mt-1 py-0'
                                style={{fontSize: '0.75rem', maxWidth: '160px'}}
                                value={node.vicidialRingStrategy || 'longest_wait_time'}
                                disabled={syncingId === node.id}
                                onChange={(e) => handleSyncTelephony(node.id, e.target.value)}
                                title={intl.formatMessage(M.ringStrategyTitle)}
                              >
                                {RING_STRATEGY_VALUES.map((value) => (
                                  <option key={value} value={value}>
                                    {ringStrategyLabel(intl, value)}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          ) : (
                            <span className='text-muted' title={intl.formatMessage(M.noNumbersTitle)}>
                              {EM_DASH}
                            </span>
                          )}
                        </td>
                        <td className='px-2 text-center'>
                          {node.visibleForTickets ? (
                            <span
                              title={intl.formatMessage(M.visibleForTickets)}
                              aria-label={intl.formatMessage(M.visibleForTickets)}
                            >
                              <i className='bi bi-check-circle-fill text-success fs-4' />
                            </span>
                          ) : (
                            <span
                              title={intl.formatMessage(M.notVisibleForTickets)}
                              aria-label={intl.formatMessage(M.notVisibleForTickets)}
                            >
                              <i className='bi bi-x-circle text-muted fs-4' />
                            </span>
                          )}
                        </td>
                        <td className='px-2'>{moment(node.created).format('DD MMM, YYYY')}</td>
                        <td className='px-2 text-center'>
                          <Button
                            size='sm'
                            variant='primary'
                            onClick={() => {
                              setInfoGruop({
                                id: node.id,
                                name: node.name,
                                members: node.user.edges,
                                supervisorIds: node.supervisors?.edges.map(({node: s}) => parseId(s.id)) || [],
                                organizationId: node.organization ? parseId(node.organization.id) : undefined,
                                visibleForTickets: Boolean(node.visibleForTickets),
                              })
                              onOpen()
                            }}
                          >
                            <i className='bi bi-pencil' /> {intl.formatMessage(M.edit)}
                          </Button>
                        </td>
                      </tr>
                      {expandedRows.has(node.id) && (
                        <tr>
                          <td colSpan={9} className='bg-light'>
                            <div className='p-3'>
                              <h6 className='mb-3 fw-bold'>{intl.formatMessage(M.membersHeading)}</h6>
                              {node.user.edges.length === 0 ? (
                                <div className='text-muted'>{intl.formatMessage(M.noMembers)}</div>
                              ) : (
                                <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                                  <ListGroup>
                                    {node.user.edges.map(({node: nodeMember}, j) => (
                                      <ListGroup.Item key={nodeMember.id}>
                                        <div className='d-flex align-items-center'>
                                          <div className='symbol symbol-circle symbol-50px overflow-hidden me-3'>
                                            <div
                                              className={clsx(
                                                'symbol-label fs-3 text-uppercase',
                                                `bg-light-${getColorMember(j)}`,
                                                `text-${getColorMember(j)}`
                                              )}
                                            >
                                              {nodeMember.firstName?.charAt(0)}
                                              {nodeMember.lastName?.charAt(0)}
                                            </div>
                                          </div>
                                          <div className='d-flex flex-column'>
                                            <span className='text-gray-800 text-hover-primary mb-1'>
                                              {memberLine(
                                                nodeMember.firstName,
                                                nodeMember.lastName,
                                                nodeMember.email
                                              )}
                                            </span>
                                            <span className='text-muted'>{nodeMember.rol}</span>
                                          </div>
                                        </div>
                                      </ListGroup.Item>
                                    ))}
                                  </ListGroup>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddAssignmentGroup
          isOpen={isOpen}
          onClose={onClose}
          infoGroup={infoGruop}
          refetch={refetch}
        />
      )}
    </div>
  )
}
